import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { Calendar, Clock, ExternalLink, Trophy, Users, UserCheck } from 'lucide-react';
import type { Region } from '@court-sync/shared';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Separator,
} from '@/components/ui';
import { SportIconTile } from '@/components/sport/SportIcons';
import { PublicPlayEventCoverImage } from '@/components/public/play/PublicPlayEventCoverImage';
import { PublicPlayEventJoinSection } from '@/components/public/play/PublicPlayEventJoinSection';
import { useOrgPublicProfile } from '@/contexts/OrgPublicProfileContext';
import { useAuthStore } from '@/store/auth';
import { playEventService, type PlayEventPublicDetailResponse } from '@/services/playEvent';
import { buildPlayEventPublicBoardPath } from '@/lib/playEventBoardPaths';
import { formatCurrency, getCurrencyFromRegion } from '@/utils/currency';
import { cn } from '@/lib/utils';
import {
  PLAY_EVENT_FORMAT_LABELS,
  PLAY_EVENT_SIDE_LABELS,
  getPublicPlayEventStatusBadgeVariant,
  getPublicPlayEventStatusLabel,
  PLAY_GENDER_RESTRICTION_LABELS,
  PLAY_JOIN_POLICY_LABELS,
  PLAY_PAYMENT_MODE_LABELS,
  SOCIAL_PLAY_MODE_LABELS,
} from '@/lib/playEventLabels';

type EventTab = 'details' | 'my-matches';

export interface PublicPlayEventDetailModalProps {
  orgSlug: string;
  eventSlug: string | null;
  isOpen: boolean;
  onClose: () => void;
  pathFor: (subpath: string) => string;
  orgRegion?: Region | string | null;
}

function formatPriceCents(
  cents: number | null | undefined,
  region?: Region | string | null
): string | null {
  if (cents == null) return null;
  const currency = getCurrencyFromRegion(
    typeof region === 'string' ? (region as Region) : (region ?? undefined)
  );
  return formatCurrency(cents / 100, currency);
}

function MetaRow({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Calendar;
  label: string;
  value: ReactNode;
}) {
  return (
    <div className="flex gap-3 text-sm">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
      <div>
        <p className="text-muted-foreground">{label}</p>
        <p className="font-medium text-foreground">{value}</p>
      </div>
    </div>
  );
}

export function PublicPlayEventDetailModal({
  orgSlug,
  eventSlug,
  isOpen,
  onClose,
  pathFor,
  orgRegion,
}: PublicPlayEventDetailModalProps) {
  const { profile } = useOrgPublicProfile();
  const { user } = useAuthStore();
  const isSignedIn = Boolean(user);
  const [activeTab, setActiveTab] = useState<EventTab>('details');
  const [detail, setDetail] = useState<PlayEventPublicDetailResponse | null>(null);
  const [myMatches, setMyMatches] = useState<
    Awaited<ReturnType<typeof playEventService.getMyMatches>>['data']
  >([]);
  const [loading, setLoading] = useState(false);

  const loadDetail = useCallback(async () => {
    if (!orgSlug || !eventSlug) return;
    setLoading(true);
    try {
      const res = await playEventService.getPublicEvent(orgSlug, eventSlug);
      if (res.success && res.data) {
        setDetail(res.data);
      } else {
        setDetail(null);
      }
    } finally {
      setLoading(false);
    }
  }, [orgSlug, eventSlug]);

  const loadMyMatches = useCallback(async () => {
    if (!orgSlug || !isSignedIn || !detail?.event.id) return;
    const res = await playEventService.getMyMatches(orgSlug, detail.event.id);
    if (res.success && res.data) {
      setMyMatches(res.data);
    }
  }, [orgSlug, isSignedIn, detail?.event.id]);

  useEffect(() => {
    if (!isOpen || !eventSlug) {
      setDetail(null);
      setActiveTab('details');
      return;
    }
    void loadDetail();
  }, [isOpen, eventSlug, loadDetail]);

  useEffect(() => {
    if (activeTab === 'my-matches' && isSignedIn && detail) {
      void loadMyMatches();
    }
  }, [activeTab, isSignedIn, detail, loadMyMatches]);

  const event = detail?.event;
  const publicStatusLabel = event ? getPublicPlayEventStatusLabel(event.status) : null;
  const publicStatusVariant = event ? getPublicPlayEventStatusBadgeVariant(event.status) : null;

  const memberPrice = formatPriceCents(event?.memberPriceCents, orgRegion);
  const studentPrice = formatPriceCents(event?.studentPriceCents, orgRegion);
  const guestPrice = formatPriceCents(event?.guestPriceCents, orgRegion);
  const hasPricing = Boolean(memberPrice || studentPrice || guestPrice);
  const hasCover = Boolean(event?.coverImageUrl?.trim());

  const eventStart = event?.startsAt
    ? event.startsAt instanceof Date
      ? event.startsAt
      : new Date(event.startsAt)
    : null;

  const statusBadges = event ? (
    <div className="flex flex-wrap items-center gap-2">
      {publicStatusLabel && publicStatusVariant ? (
        <Badge
          variant={publicStatusVariant}
          className={cn(hasCover && 'border-white/25 bg-white/15 text-white')}
        >
          {publicStatusLabel}
        </Badge>
      ) : null}
      <Badge
        variant="outline"
        className={cn('text-xs', hasCover && 'border-white/25 bg-white/10 text-white')}
      >
        {PLAY_EVENT_FORMAT_LABELS[event.format]}
      </Badge>
      <Badge
        variant="outline"
        className={cn('text-xs', hasCover && 'border-white/25 bg-white/10 text-white')}
      >
        {PLAY_EVENT_SIDE_LABELS[event.side]}
      </Badge>
    </div>
  ) : null;

  const dateTimeRow = eventStart ? (
    <div
      className={cn(
        'flex flex-col gap-1 text-sm sm:flex-row sm:items-center sm:gap-3',
        hasCover ? 'text-white/90' : 'text-muted-foreground'
      )}
    >
      <span className="inline-flex items-center gap-1.5">
        <Calendar className="h-4 w-4 shrink-0" aria-hidden />
        {format(eventStart, 'EEEE d MMMM yyyy')}
      </span>
      <span className={cn('hidden sm:inline', hasCover ? 'text-white/50' : 'text-border')} aria-hidden>
        ·
      </span>
      <span className="inline-flex items-center gap-1.5">
        <Clock className="h-4 w-4 shrink-0" aria-hidden />
        {format(eventStart, 'h:mm a')}
      </span>
    </div>
  ) : null;

  const sectionTabs = isSignedIn ? (
    <div
      className="inline-flex rounded-lg border border-border bg-background p-1"
      role="tablist"
      aria-label="Event sections"
    >
      {(
        [
          { id: 'details' as const, label: 'Details' },
          { id: 'my-matches' as const, label: 'My matches' },
        ] as const
      ).map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          aria-selected={activeTab === tab.id}
          className={cn(
            'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
            activeTab === tab.id
              ? 'bg-primary text-primary-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          )}
          onClick={() => setActiveTab(tab.id)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  ) : null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className={cn(
          'flex max-h-[min(92vh,52rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl',
          hasCover && '[&>button]:z-20 [&>button]:text-white hover:[&>button]:bg-white/15'
        )}
        overlayClassName="bg-black/60 backdrop-blur-sm"
      >
        {loading ? (
          <div className="flex min-h-[280px] items-center justify-center p-8" aria-busy="true">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
          </div>
        ) : !event ? (
          <div className="space-y-4 p-6 text-center">
            <DialogHeader>
              <DialogTitle>Event not found</DialogTitle>
              <DialogDescription>
                This event may have been removed or is no longer available.
              </DialogDescription>
            </DialogHeader>
            <Button variant="outline" onClick={onClose}>
              Back to events
            </Button>
          </div>
        ) : (
          <>
            {hasCover ? (
              <div className="relative shrink-0">
                <PublicPlayEventCoverImage event={event} hero className="rounded-none" />
                <div
                  className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-black/10"
                  aria-hidden
                />
                <div className="absolute inset-x-0 bottom-0 space-y-2 px-6 pb-5 pt-16">
                  <DialogHeader className="space-y-2 text-left">
                    <DialogTitle className="text-xl leading-tight text-white shadow-sm">
                      {event.name}
                    </DialogTitle>
                    {statusBadges}
                    <DialogDescription asChild>
                      <div>{dateTimeRow}</div>
                    </DialogDescription>
                  </DialogHeader>
                </div>
              </div>
            ) : (
              <div className="shrink-0 border-b border-border px-6 pb-4 pt-6">
                <DialogHeader className="space-y-3 text-left">
                  <div className="flex items-start gap-3">
                    <div
                      className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary"
                      aria-hidden
                    >
                      <SportIconTile sportId={event.sportId} className="h-7 w-7" />
                    </div>
                    <div className="min-w-0 flex-1 space-y-2">
                      <DialogTitle className="text-xl leading-tight">{event.name}</DialogTitle>
                      {statusBadges}
                    </div>
                  </div>
                  <DialogDescription asChild>
                    <div>{dateTimeRow}</div>
                  </DialogDescription>
                </DialogHeader>
              </div>
            )}

            {sectionTabs ? (
              <div className="shrink-0 border-b border-border px-6 py-3">{sectionTabs}</div>
            ) : null}

            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
              {activeTab === 'details' ? (
                <div className="space-y-5">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <MetaRow
                      icon={Users}
                      label="Who can join"
                      value={PLAY_JOIN_POLICY_LABELS[event.joinPolicy]}
                    />
                    <MetaRow
                      icon={Trophy}
                      label="Payment"
                      value={PLAY_PAYMENT_MODE_LABELS[event.paymentMode]}
                    />
                    {event.capacity ? (
                      <MetaRow icon={Users} label="Capacity" value={`${event.capacity} players`} />
                    ) : null}
                    {event.roundCount ? (
                      <MetaRow icon={Trophy} label="Rounds" value={String(event.roundCount)} />
                    ) : null}
                    {event.genderRestriction && event.genderRestriction !== 'open' ? (
                      <MetaRow
                        icon={UserCheck}
                        label="Gender"
                        value={PLAY_GENDER_RESTRICTION_LABELS[event.genderRestriction]}
                      />
                    ) : null}
                  </div>

                  {event.socialPlayMode ? (
                    <p className="text-sm text-muted-foreground">
                      Play mode: {SOCIAL_PLAY_MODE_LABELS[event.socialPlayMode]}
                    </p>
                  ) : null}

                  {hasPricing ? (
                    <>
                      <Separator />
                      <div>
                        <p className="mb-2 text-sm font-medium text-foreground">Pricing</p>
                        <ul className="space-y-1 text-sm text-muted-foreground">
                          {memberPrice ? <li>Members: {memberPrice}</li> : null}
                          {studentPrice ? <li>Students: {studentPrice}</li> : null}
                          {guestPrice ? <li>Guests: {guestPrice}</li> : null}
                        </ul>
                      </div>
                    </>
                  ) : null}

                  <Separator />
                  <div>
                    <p className="mb-3 text-sm font-medium text-foreground">Registration</p>
                    {eventSlug ? (
                      <PublicPlayEventJoinSection
                        orgSlug={orgSlug}
                        eventSlug={eventSlug}
                        event={event}
                        participants={detail.participants}
                        clubSlug={profile?.clubSlug}
                        academyId={profile?.academyId}
                        orgRegion={orgRegion}
                        pathFor={pathFor}
                        onJoined={() => void loadDetail()}
                      />
                    ) : null}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {!myMatches || myMatches.length === 0 ? (
                    <div className="space-y-3 text-sm text-muted-foreground">
                      <p>
                        No personal matches yet. They appear here once the draw is published and
                        matchups are visible for your registration.
                      </p>
                      <p>
                        Guests are matched by the email on their CourtSync account — use the same
                        email you entered when joining.
                      </p>
                      {eventSlug ? (
                        <Button asChild variant="outline" size="sm" className="gap-1.5">
                          <Link
                            to={buildPlayEventPublicBoardPath(orgSlug, eventSlug)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <ExternalLink className="h-4 w-4" aria-hidden />
                            View public match board
                          </Link>
                        </Button>
                      ) : null}
                    </div>
                  ) : (
                    <ul className="space-y-2">
                      {myMatches.map((match) => (
                        <li
                          key={match.id}
                          className="rounded-lg border border-border bg-muted/20 px-4 py-3 text-sm"
                        >
                          Round {match.roundNumber} — {match.result}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
